import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import settings, { paginationType } from "../../../apiConfigs/settings";
import { ReactComponent as SearchSVG } from "../../../assets/icons/search.svg";
import Pagination from "../../../components/Pagination";
import Button from "../../../designComponents/Button";
import DeleteModal from "../../../designComponents/DeleteModal";
import Input from "../../../designComponents/Input";
import Table from "../../../designComponents/Table";
import { humanReadableDate } from "../../../utils/date";
import { debounce } from "../../../utils/debounce";
import requestAPI from "../../../utils/requestAPI";
import { SearchInputWrapperStyled } from "../../Users/style";
import AddAmenity from "./AddAmenity";

type Props = {};

type AmenityType = {
  _id: string;
  title: string;
  createdAt: string;
  icon: string;
};

const Amenities = (props: Props) => {
  const navigate = useNavigate();
  const [amenitiesData, setAmenitiesData] = useState<AmenityType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [openCreateAmenityModal, setOpenCreateAmenityModal] =
    useState<boolean>(false);
  const [paginationParams, setPaginationParams] = useState({
    page: 1,
    limit: 10,
    search: "",
    totalPages: 1,
    total: 0,
  });
  const [selectedAmenity, setSelectedAmenity] = useState<AmenityType | null>(
    null
  );
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);

  const getAllAmenities = async (params: paginationType) => {
    setLoading(true);
    try {
      const query = {
        page: params?.page || 1,
        limit: params?.limit || 10,
        search: params?.search || "",
      };
      const response = await requestAPI(settings.getAmenities(query));
      setAmenitiesData(response);
      setPaginationParams((prevParams) => ({
        ...prevParams,
        totalPages: response?.pagination?.totalPages,
        total: response?.pagination?.total,
      }));
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const delayedFetch = useCallback(debounce(getAllAmenities, 300), []);

  useEffect(() => {
    delayedFetch(paginationParams);
  }, [delayedFetch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPaginationParams((prevParams) => {
      const updatedParams = {
        ...prevParams,
        search: value,
        page: 1,
      };
      delayedFetch(updatedParams);
      return updatedParams;
    });
  };

  const onCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
    setSelectedAmenity(null);
  };

  const handleCurrentPage = (page: number) => {
    delayedFetch({ ...paginationParams, page });
    setPaginationParams((prevParams) => ({ ...prevParams, page }));
  };

  const handleLimitChange = (limit: number) => {
    delayedFetch({ ...paginationParams, limit, page: 1 });
    setPaginationParams((prevParams) => ({ ...prevParams, limit, page: 1 }));
  };

  const handleEdit = (amenity: AmenityType) => {
    setSelectedAmenity(amenity);
    setOpenCreateAmenityModal(true);
  };

  const handleDelete = async (id: string) => {
    setDeleteLoading(true);
    try {
      await requestAPI(settings.deleteAmenity(id));
      getAllAmenities(paginationParams);
      setDeleteLoading(false);
      onCloseDeleteModal();
    } catch (error) {
      console.log(error);
      setDeleteLoading(false);
    }
  };
  console.log(amenitiesData, "data");
  const tableData = {
    headings: [
      { key: "avatar", label: "Avatar" },
      { key: "name", label: "Amenity Name" },
      { key: "date", label: "Created Date" },
      { key: "actions", label: "Actions" },
    ],
    body: Array.isArray(amenitiesData)
      ? amenitiesData?.map((amenity) => ({
          id: amenity._id,
          avatar: (
            <img
              src={amenity.icon}
              alt={amenity.title}
              style={{ width: "50px", height: "50px" }}
            />
          ),
          name: amenity.title,
          date: humanReadableDate(amenity.createdAt),
          actions: (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "5px",
              }}
            >
              <Button radius="normal" onClick={() => handleEdit(amenity)}>
                Edit
              </Button>
              <Button
                radius="normal"
                onClick={() => {
                  setIsOpenDeleteModal(true);
                  setSelectedAmenity(amenity);
                }}
                variant="danger"
              >
                Delete
              </Button>
            </div>
          ),
        }))
      : [],
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "25px",
        }}
      >
        <div>
          <SearchInputWrapperStyled>
            <Input
              value={paginationParams.search}
              name="providers"
              onChange={handleInputChange}
              placeholder="Search Amenity"
              iconLeft={<SearchSVG />}
            />
          </SearchInputWrapperStyled>
        </div>
        <Button radius="normal" onClick={() => setOpenCreateAmenityModal(true)}>
          Add Amenity
        </Button>
      </div>

      {openCreateAmenityModal && (
        <AddAmenity
          isOpenModal={openCreateAmenityModal}
          onClose={() => setOpenCreateAmenityModal(false)}
          initialValues={selectedAmenity || { title: "", avatar: "" }}
          refreshData={getAllAmenities}
        />
      )}

      <Table
        tableData={tableData}
        isLoading={loading}
        noData="No data to display."
        headingBgColor="#f0f0f0"
        showMenuIcon={false}
        evenRowBgColor="#fafafa"
      />
      <Pagination
        activePage={paginationParams.page}
        totalPages={paginationParams.totalPages}
        limit={paginationParams.limit}
        onCurrentPageChange={handleCurrentPage}
        onLimitChange={handleLimitChange}
      />

      {isOpenDeleteModal && (
        <DeleteModal
          isOpen={true}
          itemName={selectedAmenity?.title || ""}
          onDelete={() =>
            selectedAmenity?._id && handleDelete(selectedAmenity?._id)
          }
          onClose={onCloseDeleteModal}
          title="Are you sure want to delete this amenity?"
          loading={deleteLoading}
        />
      )}
    </div>
  );
};

export default Amenities;
